import { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/feature/Toast';
import { useAuth } from '@/context/AuthContext';
import { generarPdfHistorialGeneral, generarPdfHistorialEmpleado } from '@/lib/pdf/historialVentasPdf';
import { generarComprobanteVenta } from '@/lib/pdf/comprobantePdf';
import type { DetalleItem, Periodo, UsuarioInfo, VentaRow } from '@/lib/ventas/types';
import { SIN_USUARIO, agruparPorEmpleado, filtrarPorRango, getPeriodoRango } from '@/lib/ventas/periodo';
import ResumenEmpleados from '@/pages/ventas/components/ResumenEmpleados';
import DescargaEmpleadoModal from '@/pages/ventas/components/DescargaEmpleadoModal';
import DetalleVentaModal from '@/pages/ventas/components/DetalleVentaModal';
import VentasTabla from '@/pages/ventas/components/VentasTabla';

const periodos: { key: Periodo; label: string }[] = [
  { key: 'hoy', label: 'Hoy' },
  { key: 'semana', label: 'Esta Semana' },
  { key: 'mes', label: 'Este Mes' },
];

function formatBs(val: number): string {
  return `Bs. ${Number(val || 0).toFixed(2)}`;
}

export default function VentasPage() {
  const { addToast } = useToast();
  const { user } = useAuth();

  const [ventas, setVentas] = useState<VentaRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [periodo, setPeriodo] = useState<Periodo>('hoy');
  const [metodoFilter, setMetodoFilter] = useState('');

  const [detalleOpen, setDetalleOpen] = useState(false);
  const [selectedVenta, setSelectedVenta] = useState<VentaRow | null>(null);
  const [detalleLoading, setDetalleLoading] = useState(false);

  const [exportingId, setExportingId] = useState<string | null>(null);
  const [exportandoGeneral, setExportandoGeneral] = useState(false);

  const [empleadoOpen, setEmpleadoOpen] = useState(false);
  const [empleadoKey, setEmpleadoKey] = useState('');
  const [periodoModal, setPeriodoModal] = useState<Periodo>('hoy');
  const [exportandoEmpleado, setExportandoEmpleado] = useState(false);

  const loadVentas = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: ventasData, error: ventasError } = await supabase
        .from('ventas')
        .select('*')
        .order('fecha', { ascending: false });

      if (ventasError) throw ventasError;

      const filas = ventasData || [];
      const ids = [...new Set(filas.map((v: { usuario_id: string | null }) => v.usuario_id).filter(Boolean))] as string[];
      const usuariosMap: Record<string, UsuarioInfo> = {};

      if (ids.length > 0) {
        const { data: usersData } = await supabase
          .from('usuarios')
          .select('id, nombre, usuario, rol')
          .in('id', ids);
        (usersData || []).forEach((u: UsuarioInfo) => {
          usuariosMap[u.id] = u;
        });
      }

      const mapped: VentaRow[] = filas.map((v: Record<string, unknown>) => {
        const u = v.usuario_id ? usuariosMap[v.usuario_id as string] : undefined;
        return {
          ...(v as unknown as VentaRow),
          usuario_nombre: u?.nombre || SIN_USUARIO,
          usuario_login: u?.usuario || '—',
          usuario_rol: u?.rol || '—',
        };
      });

      setVentas(mapped);
    } catch {
      setError('No se pudo cargar el historial de ventas. Revisá la conexión e intentá de nuevo.');
      addToast('Error al cargar el historial de ventas', 'error');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    loadVentas();
  }, [loadVentas]);

  const rango = useMemo(() => getPeriodoRango(periodo), [periodo]);
  const ventasPeriodo = useMemo(() => filtrarPorRango(ventas, rango), [ventas, rango]);
  const resumenEmpleados = useMemo(() => agruparPorEmpleado(ventasPeriodo), [ventasPeriodo]);

  const periodoStats = useMemo(() => {
    return ventasPeriodo.reduce(
      (acc, v) => {
        const monto = Number(v.total) || 0;
        acc.total += monto;
        acc.cantidad += 1;
        if (v.metodo_pago === 'Efectivo') acc.efectivo += monto;
        else if (v.metodo_pago === 'QR') acc.qr += monto;
        return acc;
      },
      { total: 0, cantidad: 0, efectivo: 0, qr: 0 },
    );
  }, [ventasPeriodo]);

  const filtered = useMemo(() => {
    let result = [...ventasPeriodo];
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (v) =>
          (v.nombre_nit || '').toLowerCase().includes(q) ||
          (v.numero_nit || '').toLowerCase().includes(q) ||
          v.metodo_pago.toLowerCase().includes(q) ||
          v.usuario_nombre.toLowerCase().includes(q) ||
          (v.usuario_login || '').toLowerCase().includes(q),
      );
    }
    if (metodoFilter) result = result.filter((v) => v.metodo_pago === metodoFilter);
    return result;
  }, [ventasPeriodo, search, metodoFilter]);

  const empleadosModal = useMemo(
    () => agruparPorEmpleado(filtrarPorRango(ventas, getPeriodoRango(periodoModal))),
    [ventas, periodoModal],
  );

  const handleViewDetalle = async (venta: VentaRow) => {
    setSelectedVenta(venta);
    setDetalleOpen(true);
    if (venta.detalle) return;

    setDetalleLoading(true);
    try {
      const { data: detData, error: detError } = await supabase
        .from('detalle_venta')
        .select('*')
        .eq('venta_id', venta.id);
      if (detError) throw detError;

      const rows = detData || [];
      const prodIds = [...new Set(rows.map((d: { producto_id: string }) => d.producto_id))];
      const { data: prodsData } = await supabase.from('productos').select('id, nombre').in('id', prodIds);

      const nameMap: Record<string, string> = {};
      (prodsData || []).forEach((p: { id: string; nombre: string }) => {
        nameMap[p.id] = p.nombre;
      });

      const detalleCompleto = rows.map((d: Record<string, unknown>) => ({
        ...d,
        producto_nombre: nameMap[d.producto_id as string] || 'Desconocido',
      })) as DetalleItem[];

      setSelectedVenta((prev) => (prev ? { ...prev, detalle: detalleCompleto } : null));
      setVentas((prev) => prev.map((v) => (v.id === venta.id ? { ...v, detalle: detalleCompleto } : v)));
    } catch {
      addToast('Error al cargar el detalle', 'error');
    } finally {
      setDetalleLoading(false);
    }
  };

  const handleExportComprobante = async (venta: VentaRow) => {
    setExportingId(venta.id);
    try {
      let ventaConDetalle = venta;
      if (!venta.detalle || venta.detalle.length === 0) {
        const { data: detData, error: detError } = await supabase
          .from('detalle_venta')
          .select('*')
          .eq('venta_id', venta.id);

        if (!detError && detData && detData.length > 0) {
          const prodIds = [...new Set(detData.map((d: { producto_id: string }) => d.producto_id))];
          const { data: prodsData } = await supabase.from('productos').select('id, nombre').in('id', prodIds);
          const nameMap: Record<string, string> = {};
          (prodsData || []).forEach((p: { id: string; nombre: string }) => {
            nameMap[p.id] = p.nombre;
          });
          const detalleCompleto = detData.map((d: Record<string, unknown>) => ({
            ...d,
            producto_nombre: nameMap[d.producto_id as string] || 'Desconocido',
          })) as DetalleItem[];
          ventaConDetalle = { ...venta, detalle: detalleCompleto };
          setVentas((prev) => prev.map((v) => (v.id === venta.id ? ventaConDetalle : v)));
        }
      }
      await generarComprobanteVenta(ventaConDetalle);
      addToast('Comprobante descargado', 'success');
    } catch {
      addToast('Error al generar comprobante', 'error');
    } finally {
      setExportingId(null);
    }
  };

  const handleExportGeneral = async () => {
    if (exportandoGeneral || ventasPeriodo.length === 0) return;
    setExportandoGeneral(true);
    try {
      await generarPdfHistorialGeneral(periodo, rango, ventasPeriodo, resumenEmpleados, user?.nombre || 'Sistema');
      addToast('PDF del historial generado', 'success');
    } catch {
      addToast('Error al generar el PDF del historial', 'error');
    } finally {
      setExportandoGeneral(false);
    }
  };

  const handleExportEmpleado = async () => {
    if (exportandoEmpleado || empleadosModal.length === 0) return;
    const empleado = empleadosModal.find((e) => e.key === empleadoKey) || empleadosModal[0];
    if (!empleado) return;

    setExportandoEmpleado(true);
    try {
      const rangoModal = getPeriodoRango(periodoModal);
      const ventasEmp = filtrarPorRango(ventas, rangoModal).filter(
        (v) => (v.usuario_id || 'sin-usuario') === empleado.key,
      );
      await generarPdfHistorialEmpleado(empleado, periodoModal, rangoModal, ventasEmp, user?.nombre || 'Sistema');
      addToast(`PDF de ${empleado.nombre} generado`, 'success');
      setEmpleadoOpen(false);
    } catch {
      addToast('Error al generar el PDF del empleado', 'error');
    } finally {
      setExportandoEmpleado(false);
    }
  };

  const openEmpleadoModal = () => {
    setEmpleadoKey(resumenEmpleados[0]?.key || '');
    setPeriodoModal(periodo);
    setEmpleadoOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground-800">Historial de Ventas</h1>
          <p className="text-foreground-500 text-sm mt-1">
            {ventasPeriodo.length} venta{ventasPeriodo.length !== 1 ? 's' : ''} en {rango.label.toLowerCase()}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <button
            onClick={handleExportGeneral}
            disabled={exportandoGeneral || ventasPeriodo.length === 0}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-500 hover:bg-primary-600 disabled:bg-background-300 disabled:text-foreground-400 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap text-sm"
          >
            {exportandoGeneral ? (
              <><i className="ri-loader-4-line animate-spin"></i><span>Generando PDF...</span></>
            ) : (
              <><i className="ri-file-pdf-line"></i><span>Descargar PDF</span></>
            )}
          </button>
          <button
            onClick={openEmpleadoModal}
            disabled={resumenEmpleados.length === 0}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-background-200 hover:border-primary-300 hover:text-primary-600 disabled:text-foreground-400 disabled:cursor-not-allowed text-foreground-700 font-medium rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap text-sm"
          >
            <i className="ri-user-received-line"></i>
            <span>Descargar por empleado</span>
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {periodos.map((p) => (
          <button
            key={p.key}
            onClick={() => setPeriodo(p.key)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
              periodo === p.key ? 'bg-primary-500 text-white' : 'bg-background-100 text-foreground-600 hover:bg-background-200'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-primary-500 rounded-xl p-5 sm:col-span-2">
          <div className="flex items-center gap-2 text-white/80 mb-1">
            <i className="ri-wallet-3-line"></i>
            <span className="text-xs font-medium uppercase tracking-wide">Total de Ventas · {rango.label}</span>
          </div>
          <p className="font-heading text-3xl font-bold text-white">{formatBs(periodoStats.total)}</p>
          <p className="text-white/70 text-xs mt-1">{periodoStats.cantidad} ventas registradas en el período</p>
        </div>

        <div className="bg-white rounded-xl border border-background-200 p-5">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-9 h-9 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center">
              <i className="ri-money-dollar-circle-line"></i>
            </div>
            <span className="text-xs text-foreground-500">Efectivo</span>
          </div>
          <p className="font-heading text-lg font-bold text-foreground-800">{formatBs(periodoStats.efectivo)}</p>
        </div>

        <div className="bg-white rounded-xl border border-background-200 p-5">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-9 h-9 rounded-lg bg-accent-100 text-accent-600 flex items-center justify-center">
              <i className="ri-qr-code-line"></i>
            </div>
            <span className="text-xs text-foreground-500">QR</span>
          </div>
          <p className="font-heading text-lg font-bold text-foreground-800">{formatBs(periodoStats.qr)}</p>
        </div>
      </div>

      <ResumenEmpleados empleados={resumenEmpleados} periodoLabel={rango.label} />

      <div className="bg-white rounded-xl border border-background-200 p-4">
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por cliente, NIT, método o empleado..."
              className="w-full pl-10 pr-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-foreground-800 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setMetodoFilter('')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                metodoFilter === '' ? 'bg-foreground-800 text-white' : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              Todos
            </button>
            <button
              onClick={() => setMetodoFilter('Efectivo')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                metodoFilter === 'Efectivo' ? 'bg-primary-500 text-white' : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              Efectivo
            </button>
            <button
              onClick={() => setMetodoFilter('QR')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                metodoFilter === 'QR' ? 'bg-accent-500 text-white' : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              QR
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-3">
              <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
              <p className="text-foreground-500 text-sm">Cargando historial...</p>
            </div>
          </div>
        ) : error ? (
          <div className="text-center py-16 px-6">
            <div className="w-16 h-16 bg-accent-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <i className="ri-error-warning-line text-3xl text-accent-500"></i>
            </div>
            <h3 className="text-lg font-semibold text-foreground-700 mb-1">No se pudo cargar el historial</h3>
            <p className="text-foreground-400 text-sm max-w-md mx-auto mb-4">{error}</p>
            <button
              onClick={loadVentas}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap text-sm"
            >
              <i className="ri-refresh-line"></i>
              <span>Reintentar</span>
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-background-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <i className="ri-receipt-line text-3xl text-foreground-300"></i>
            </div>
            <h3 className="text-lg font-semibold text-foreground-600 mb-1">Sin ventas registradas</h3>
            <p className="text-foreground-400 text-sm">
              {search || metodoFilter
                ? 'No se encontraron ventas con los filtros aplicados'
                : 'No hay ventas en el período seleccionado'}
            </p>
          </div>
        ) : (
          <VentasTabla
            ventas={filtered}
            exportingId={exportingId}
            onVerDetalle={handleViewDetalle}
            onComprobante={handleExportComprobante}
          />
        )}
      </div>

      <DetalleVentaModal
        venta={detalleOpen ? selectedVenta : null}
        loading={detalleLoading}
        onClose={() => {
          setDetalleOpen(false);
          setSelectedVenta(null);
        }}
      />

      <DescargaEmpleadoModal
        open={empleadoOpen}
        onClose={() => setEmpleadoOpen(false)}
        empleados={empleadosModal}
        selectedKey={empleadoKey}
        onSelectKey={setEmpleadoKey}
        periodo={periodoModal}
        onSelectPeriodo={setPeriodoModal}
        generando={exportandoEmpleado}
        onDownload={handleExportEmpleado}
      />
    </div>
  );
}